<template>
  <div class="utm-setting"
       :class="{ confirming } ">
    <div class="confirm">
      <div class="confirm-icon">
        <i class="icon icon--dialog-warning"></i>
      </div>
      <div class="confirm-title"
           v-html="confirmOption.title"></div>
      <div class="confirm-description"
           v-html="confirmOption.description"></div>
      <div class="confirm-buttons">
        <ui-button @click="confirming = false">
          {{ $t('common.no') }}
        </ui-button>
        <ui-button @click="confirmOption.confirm"
                   :primary2="true">
          {{ $t('common.yes') }}
        </ui-button>
      </div>
    </div>
    <transition name="fade">
      <div class="step loading"
           v-show="loading">
        <ba-loading :showLoadingText="true" />
      </div>
    </transition>
    <div class="step step-1"
         v-show="step <= 1">
      <div class="utm-header">
        <h2 v-html="$t('journey.targeting.utm.title')"></h2>
        <p v-html="$t('journey.targeting.utm.description')"></p>
      </div>
      <div class="utm-body">
        <div class="utm-description">
          <i class="icon icon--ga"></i>
          <h3>
            <span v-html="$t('journey.targeting.utm.step1.title')"></span>
            <ui-icon-tooltip :iconSize="'outline-white'"
                             arrowBottom
                             arrowCenter>
              <span v-html="$t('journey.targeting.utm.step1.tooltip')"></span>
            </ui-icon-tooltip>
          </h3>
          <p v-html="$t('journey.targeting.utm.step1.description')"></p>
        </div>
        <div v-if="hasData"
             class="utm-selection">
          <button v-for="item of tagList"
                  :key="`utmButton${item.tag}`"
                  @click="showList(item.tag)"
                  :disabled="item.on"
                  :class="{ selected: selectedUtm[item.tag] && selectedUtm[item.tag].length > 0, disabled: item.on, on: !item.on }">
            <ui-tooltip v-if="item.on" arrowBottom arrowCenter><span v-html="$t('journey.targeting.youAreFree')"></span></ui-tooltip>
            <span class="name"
                  :class="{ disabled: item.on}"
                  v-html="$t('journey.targeting.utm.' + item.tag)"></span>
            <span v-if="selectedUtm[item.tag] && selectedUtm[item.tag].length > 0"
                  class="count">
              <span class="check">
                <i class="icon icon--check-white"></i>
              </span>
              <span class="number">
                {{ selectedUtm[item.tag].length | thousand }}
              </span>
              {{ $t('common.selected') }}
            </span>
            <i v-else
               class="icon icon--plus" :style="{ opacity: item.on ? 0.5 : 1 }"></i>
          </button>
        </div>
        <div v-else
             class="utm-empty">
          <i class="icon loading--empty"></i><br>
          {{ $t('common.noCollectedData') }}
        </div>
      </div>
      <div class="utm-footer">
        <ui-button @click="onCloseLayer(false)">{{ $t('common.confirm') }}</ui-button>
      </div>
    </div>
    <transition name="fade">
      <div class="step step-2"
           v-if="step === 2">
        <div class="utm-header">
          <button type="button"
                  class="btn-close"
                  @click="onCloseLayer(true)">
            <i class="icon icon--close-size16"></i>
          </button>
          <h2 v-html="$t('journey.targeting.utm.step2.title', { tag: selectedTag })"></h2>
          <p v-html="$t('journey.targeting.utm.step2.description', { tag: selectedTag })"></p>
        </div>
        <div class="utm-body">
          <ui-check-selectbox v-if="listItems"
                              :width="'100%'"
                              :height="'362px'"
                              :full="true"
                              :maxHeight="362"
                              :search="true"
                              :value="selectingItem"
                              :items="items"
                              :unselectLastOne="true"
                              @input="onSelectedUtmChange"/>
          <div v-else
              class="empty">
            <i class="icon loading--empty"></i><br>
            {{ $t('common.noCollectedData') }}
          </div>
        </div>
        <div class="utm-footer">
          <ui-button class="back"
                     :iconName="'icon--arrow-left-black'"
                     @click="showIndex">{{ $t('common.back') }}</ui-button>
          <ui-button @click="resetSelected"
                     :iconName="'icon--reset'"
                     :disabled="!selectingItem || selectingItem.length < 1">{{ $t('common.reset') }}</ui-button>
          <ui-button @click="applySelected"
                     :disabled="JSON.stringify(selectingItem) === JSON.stringify(selectedUtm[selectedTag])"
                     :primary2="true">{{ $t('common.apply') }}</ui-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { addDays, format } from 'date-fns'
import { mapMutations, mapState } from 'vuex'
import { compare09azAZ, isInRanges } from '@/libs/utils'
import { getUtmParameter } from '@/api/journey'

import UiButton from '@/components/ui/UiButton'
import UiCheckSelectbox from '@/components/ui/UiCheckSelectbox'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiTooltip from '@/components/ui/UiTooltip'
import BaLoading from '@/components/ui/ba/Loading'

export default {
  name: 'UtmSetting',
  components: {
    UiButton,
    UiCheckSelectbox,
    UiIconTooltip,
    UiTooltip,
    BaLoading
  },
  props: ['utmCache', 'tag'],
  methods: {
    ...mapMutations([
      'setJourneyOptions',
      'setJourneyExcelData'
    ]),
    showList (tag) {
      this.selectedTag = tag
      this.setUtmData(this.utmData)
    },
    setUtmData (result) {
      this.setJourneyExcelData({ utm: result })
      let listItems = {}
      let hasItems = false
      this.selectingItem = [...(this.selectedUtm[this.selectedTag] || [])]
      for (const device of Object.values(result)) {
        if (device[this.selectedTagKey]) {
          device[this.selectedTagKey].forEach(pair => {
            for (const [key, value] of Object.entries(pair)) {
              listItems[key] = value
              hasItems = true
            }
          })
        }
      }
      if (hasItems) {
        this.listItems = { '': {} }
        this.listItems[''][this.$t('journey.targeting.utm.nullInflow', { tag: this.selectedTag })] = null

        if (listItems[''] !== undefined) {
          this.listItems[''][this.$t('journey.targeting.utm.blankInflow', { tag: this.selectedTag })] = 0
        }

        const keys = Object.keys(listItems)
        keys.sort(compare09azAZ)
        keys.forEach(key => {
          if (key && listItems[key]) {
            const char = key.charAt(0).toLowerCase()
            if (!this.listItems[char]) {
              this.listItems[char] = {}
            }
            this.listItems[char][key] = listItems[key]
          }
        })
        this.selectingItem.sort()
      } else {
        this.listItems = false
      }
      this.step = 2
      this.loading = false
    },
    onSelectedUtmChange (value) {
      this.selectingItem = value
      this.selectingItem.sort()
    },
    showIndex () {
      if (this.selectedTag && JSON.stringify(this.selectedUtm[this.selectedTag]) !== JSON.stringify(this.selectingItem)) {
        this.showConfirm(this.$t('dialog.journey.noAppliedAndGoBack.title'), this.$t('dialog.journey.noAppliedAndGoBack.desc'), () => {
          this.resetSelected()
          this.step = 1
          this.selectedTag = null
          this.confirming = false
        })
      } else {
        this.step = 1
        this.selectedTag = null
      }
    },
    resetSelected () {
      this.selectingItem = []
    },
    applySelected () {
      this.selectedUtm[this.selectedTag] = [...this.selectingItem]
      this.step = 1
      this.selectedTag = null
    },
    onCloseLayer (reset) {
      let selectedUtm = this.selectedUtm
      let selectedUtmExist = false
      if (selectedUtm) {
        for (const item of this.tagList) {
          if (selectedUtm[item.tag] && selectedUtm[item.tag].length > 0) {
            selectedUtmExist = true
          }
        }
      }
      if (!selectedUtmExist) {
        selectedUtm = null
      }
      if (reset) {
        if (JSON.stringify(selectedUtm) !== JSON.stringify(this.initialUtm) ||
          (this.selectedTag && ((!this.initialUtm && this.selectingItem.length > 0) || (this.initialUtm && JSON.stringify(this.initialUtm[this.selectedTag]) !== JSON.stringify(this.selectingItem))))) {
          this.showConfirm(this.$t('dialog.journey.noAppliedAndClose.title'), this.$t('dialog.journey.noAppliedAndClose.desc'), () => { this.$emit('onCloseLayer') })
        } else {
          this.$emit('onSetUtm', selectedUtm)
        }
      } else {
        this.$emit('onSetUtm', selectedUtm)
      }
    },
    showConfirm (title, description, confirm) {
      this.confirmOption.title = title
      this.confirmOption.description = description
      this.confirmOption.confirm = confirm
      this.confirming = true
    },
    isAllDaysEveryone () {
      const paidRange = this.planData.paidRange
      const rangeStart = this.journeyPeriod.start
      const rangeEnd = this.journeyPeriod.end
      let allday = true
      for (let i = 0; i < 1000; i++) {
        const date = addDays(rangeStart, i)
        if (isInRanges(date, paidRange)) {
          allday = false
        }
        if (format(date, 'YYYY-MM-DD') === rangeEnd) {
          break
        }
      }
      return allday
    }
  },
  created () {
    this.initialUtm = this.utmCache
    this.selectedUtm = {
      source: this.initialUtm ? this.initialUtm.source : [],
      medium: this.initialUtm ? this.initialUtm.medium : [],
      campaign: this.initialUtm ? this.initialUtm.campaign : [],
      term: this.initialUtm ? this.initialUtm.term : [],
      content: this.initialUtm ? this.initialUtm.content : []
    }

    if (!this.utmData) {
      this.loading = true

      setTimeout(() => {
        if (this.step === 0) {
          this.loading = true
        }
      }, 100)

      const params = {
        start: this.journeyPeriod.start,
        end: this.journeyPeriod.end,
        sid: this.journeyOptions.sid,
        matchType: this.journeyOptions.matchType
      }

      if (this.journeyOptions.subSid) {
        params.subSid = this.journeyOptions.subSid
        params.firstPathname = this.journeyOptions.firstPath
      }

      getUtmParameter(params, false)
        .then(result => {
          this.loading = false
          this.utmData = result
          if (this.tag && this.tag !== true) {
            this.showList(this.tag)
          } else {
            this.step = 1
          }
        })
    }
  },
  computed: {
    ...mapState([
      'journeyPeriod',
      'journeyOptions',
      'planData'
    ]),
    hasData () {
      if (!this.utmData) {
        return false
      }
      let has = false
      Object.values(this.utmData).forEach(device => {
        if (typeof device === 'object') {
          Object.values(device).forEach(params => {
            if (params.length > 0) {
              has = true
            }
          })
        }
      })
      return has
    },
    selectedTagKey () {
      return 'UTM_' + this.selectedTag.toUpperCase()
    },
    items () {
      if (!this.listItems) {
        return false
      }

      let list = Object.entries(this.listItems)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, value]) => {
          return {
            value: key,
            text: key,
            items: Object.entries(value).map(([k, v]) => {
              return {
                value: v,
                text: k
              }
            })
          }
        })

      return list
    },
    tagList () {
      const taglist = [
        {
          tag: 'source',
          on: false
        },
        {
          tag: 'medium',
          on: this.isAllDaysEveryone()
        },
        {
          tag: 'campaign',
          on: this.isAllDaysEveryone()
        },
        {
          tag: 'term',
          on: this.isAllDaysEveryone()
        },
        {
          tag: 'content',
          on: this.isAllDaysEveryone()
        }
      ]
      return taglist
    }
  },
  data () {
    return {
      step: 0,
      loading: false,
      initialUtm: null,
      selectedUtm: {},
      selectedTag: null,
      listItems: false,
      selectingItem: [],
      confirming: false,
      confirmOption: {
        title: '',
        description: '',
        confirm: () => {}
      },
      utmData: null
    }
  }
}
</script>

<style lang="scss" scoped>
.utm-setting {
  width: 830px;
  height: 543px;
  border-radius: 14px;
  box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  transition: width 0.2s cubic-bezier(.28,.12,.42,.94),
              height 0.2s cubic-bezier(.28,.12,.42,.94);
  overflow: hidden;

  &.confirming {
    position: relative;
    width: 430px;
    height: 280px;

    .confirm {
      opacity: 1;
      z-index: 10;
    }

    .step {
      opacity: 0;
      z-index: 1;
    }
  }

  .confirm {
    position: relative;
    text-align: center;
    opacity: 0;
    transition: opacity 0.2s ease;

    .confirm-icon {
      margin-top: 50px;
    }

    .confirm-title {
      margin-top: 13px;
      font-size: 20px;
      color: #444444;
    }

    .confirm-description {
      font-size: 14px;
      color: #444444;
      line-height: 1.43;
      margin-top: 13px;
      height: 70px;
    }

    .confirm-buttons {
      .ui-btn {
        margin: 0 2px 0 3px;
        width: 86px;
      }
    }
  }

  .step {
    position: absolute;
    top: 0;
    left: 0;
    width: 830px;
    height: 543px;
    background-color: #ffffff;
    border-radius: 15px;
    opacity: 1;
    transition: opacity 0.2s ease;

    &.fade-enter-active,
    &.fade-leave-active {
      opacity: 1;
    }
    &.fade-enter,
    &.fade-leave-to{
      opacity: 0;
    }

    &.loading {
      z-index: 100;
      top: 29%;
      width: 100%;
      height: 55%;
    }

    &.step-1 {
      .utm-header {
        margin: 0 30px;
        border-bottom: 1px solid #bbbbbb;
        text-align: center;

        h2 {
          font-size: 20px;
          font-weight: 600;
          color: #444444;
          margin: 42px 0 13px;
        }

        p {
          font-size: 13px;
          line-height: 1.08;
          color: #888888;
          margin: 13px 0 30px;
        }
      }

      .utm-body {
        margin: 0 30px;

        .utm-description {
          position: relative;
          margin-top: 30px;
          padding: 30px 30px 30px 131px;

          .icon {
            @include positionYcenter;
            left: 39px;
          }

          h3 {
            font-size: 18px;
            font-weight: 600;
            color: #444444;
            line-height: 1.17;
            margin-bottom: 10px;

            .tooltip {
              top: 2px;
              left: 5px;
              font-weight: 400;

              & /deep/ .bubble {
                margin-bottom: 3px !important;
                margin-left: -10.5px !important;
              }
            }
          }

          p {
            font-size: 13px;
            color: #444444;
            line-height: 1.38;

            & /deep/ span {
              color: #ec0047;
            }
          }
        }

        .utm-selection {
          text-align: center;
          margin: 5px 0;

          button {
            position: relative;
            vertical-align: top;
            width: 136px;
            height: 136px;
            margin: 1px 6px;
            border: 2px dotted #666666;
            border-radius: 10px;
            transition: border-color 0.2s ease,
                        background-color 0.2s ease,
                        box-shadow 0.2s ease;

            .name {
              font-size: 16px;
              color: #444444;
              display: block;
              margin: 5px 0 12px;
              &.disabled {
              color: #c7c7c7;
              }
            }

            .count {
              font-size: 12px;
              color: #444444;
              margin-top: -5px;
              margin-bottom: 10px;
              display: block;
              text-align: center;

              .number {
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 7px;
                display: block;
                color: #3eaa08;
              }

              .check {
                position: absolute;
                top: -1px;
                right: -1px;
                width: 19px;
                height: 19px;
                padding: 4px 4px 16px 16px;
                border-top-right-radius: 10px;
                background: url('../../assets/img/journey/utm-button-bg.svg') transparent no-repeat right -30px top -30px;
                transition: background-position 0.2s ease;

                i {
                  width: 16px;
                  height: 16px;
                }
              }
            }

            i {
              width: 20px;
              height: 20px;
            }

            &.selected {
              border: 2px solid #3eaa08;
              background-color: rgba(62, 170, 8, 0.03);

              .count {
                .check {
                  background-position: right 0 top 0;
                }
              }
            }

            &.disabled {
              border: 2px dotted #c7c7c7;
              color: #c7c7c7;
              .bubble {
                width: auto;
                height: auto;
                top: -64px;
                left: -64px;
                display: none;
              }

              &:hover {
                .bubble {
                  display: block;
                }
              }
            }

            &.on:hover {
              border: 2px solid #0274ff;
              background-color: #ffffff;
              box-shadow: 0 8px 12px -2px rgba(0, 0, 0, 0.25);

              .count {
                .check {
                  background-position: right -30px top -30px;
                }
              }
            }
          }
        }

        .utm-empty {
          height: 148px;
          text-align: center;
          line-height: 1.46;
          font-size: 13px;
          color: #555555;

          i {
            margin-top: -23px;
          }
        }
      }

      .utm-footer {
        margin: 62px 0 20px;
        text-align: center;

        .ui-btn {
          width: 95px;
        }
      }
    }

    &.step-2 {
      .utm-header {
        position: relative;
        margin: 0 30px;

        h2 {
          font-size: 20px;
          font-weight: 600;
          color: #444444;
          margin: 30px 0 10px;

          & /deep/ span {
            color: #ec0047;
          }
        }

        p {
          font-size: 13px;
          line-height: 1.08;
          color: #888888;
          margin: 10px 0 22px;
        }

        .btn-close {
          position: absolute;
          top: -18px;
          right: -18px;
        }
      }

      .utm-body {
        padding: 0 30px;

        .empty {
          width: 100%;
          height: 362px;
          border-radius: 5px;
          border: 1px solid #d7d7d7;
          background-color: #f4f4f4;
          line-height: 1.77;
          color: #666666;
          font-size: 14px;
          text-align: center;

          i {
            margin: 72px 0 -8px 0;
          }
        }
      }

      .utm-footer {
        margin: 20px 15px;
        text-align: center;
        position: relative;

        .ui-btn {
          margin: 0 2px 0 3px;

          &.back {
            position: absolute;
            top: 0;
            left: 0;
            border: none;
            font-size: 13px;
            width: 75px;
          }
        }
      }
    }
  }
}
</style>
